import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './PwaInstall.css';

interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
const InstallContext = createContext<{
  available: boolean; standalone: boolean; busy: boolean; message: string; install: () => Promise<void>;
}>({ available: false, standalone: false, busy: false, message: '', install: async () => {} });

export function PwaInstallProvider({ children }: { children: React.ReactNode }) {
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);
  const [standalone, setStandalone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    const updateDisplay = () => setStandalone(media.matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
    updateDisplay();
    const beforeInstall = (event: Event) => { event.preventDefault(); setPrompt(event as InstallPrompt); setMessage(''); };
    const installed = () => { setPrompt(null); setMessage('Приложение установлено. Откройте AvatarMe с главного экрана.'); };
    window.addEventListener('beforeinstallprompt', beforeInstall);
    window.addEventListener('appinstalled', installed);
    media.addEventListener('change', updateDisplay);
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstall);
      window.removeEventListener('appinstalled', installed);
      media.removeEventListener('change', updateDisplay);
    };
  }, []);
  const install = async () => {
    if (!prompt || busy) return;
    setBusy(true);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      setMessage(choice.outcome === 'accepted' ? 'Установка подтверждена. Дождитесь значка AvatarMe на главном экране.' : 'Установка отменена. Можно установить приложение позже через меню браузера.');
    } catch {
      setMessage('Не удалось открыть установку. Попробуйте через меню браузера по инструкции ниже.');
    } finally {
      setPrompt(null);
      setBusy(false);
    }
  };
  return <InstallContext.Provider value={{ available: Boolean(prompt), standalone, busy, message, install }}>{children}</InstallContext.Provider>;
}

export default function PwaInstall() {
  const { available, standalone, busy, message, install } = useContext(InstallContext);
  return <main className="pwa-page">
    <Link className="nav-action" to="/">← На главную</Link>
    <section className="pwa-card">
      <img className="pwa-icon" src="/app-icon-192.png" width="80" height="80" alt="" />
      <p className="pwa-eyebrow">AVATARME НА ТЕЛЕФОНЕ</p>
      <h1>Ваши цели — под рукой</h1>
      <p>Тот же AvatarMe: дела, здоровье, семья, баланс и Аватар. С привычным интерфейсом, вашим аккаунтом и общими данными.</p>
      <p>Значок на главном экране открывает приложение в отдельном окне. Для работы нужен интернет.</p>
      {standalone ? <p role="status" className="pwa-message">Вы уже открыли AvatarMe как приложение.</p> : <>
        {available && <button className="pwa-install-button" disabled={busy} onClick={() => { void install(); }}>{busy ? 'Открываем установку…' : 'Установить AvatarMe'}</button>}
        {message && <p role="status" className="pwa-message">{message}</p>}
        {!available && !message && <p className="pwa-hint">Установите приложение через меню браузера. Если браузер предложит быструю установку, здесь появится кнопка.</p>}
        <div className="pwa-instructions">
          <section><h2>Android</h2><ol><li>Откройте этот сайт в Chrome.</li><li>Нажмите «Установить AvatarMe» или откройте меню ⋮ → «Установить приложение» / «Добавить на главный экран».</li><li>Подтвердите установку.</li></ol></section>
          <section><h2>iPhone и iPad</h2><ol><li>Откройте этот сайт в Safari.</li><li>Нажмите «Поделиться» → «На экран Домой».</li><li>Если есть переключатель «Открывать как веб-приложение», включите его, затем нажмите «Добавить».</li></ol></section>
        </div>
        <p className="pwa-hint">Если вы открыли ссылку внутри мессенджера, перейдите в обычный браузер. После установки при необходимости войдите в свой аккаунт.</p>
      </>}
      <Link className="nav-action" to="/goals">Перейти к целям →</Link>
    </section>
  </main>;
}
